import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useEndpoints } from "../../api/ApiContext";
import type { Endpoints } from "../../api/endpoints";
import type {
  CreativeFormatKey,
  CreativeValidation,
  CreativeValidationRequest,
  PricingModel,
  TargetingCategory,
  TargetingPublisher,
  ValidationIssue,
} from "../../api/types";
import { Icon } from "../../components/Icon";
import { Banner, Button, Checkbox, ErrorState, Input, RadioGroup, SkeletonBlock, StatusPill } from "../../components/ui";
import { categoryLabel, useTargetingCategories } from "../../lib/targetingCategories";

// Re-exported so CampaignCandidate.tsx/CampaignWizard.tsx's existing
// `from "./campaignInputs"` imports keep working; the vocabulary read
// itself now lives in lib/targetingCategories.ts, shared with the
// publisher portal (SiteDetail.tsx).
export { categoryLabel, useTargetingCategories };

// Inputs shared by the campaign wizard (UX-DESIGN.md §2.3 steps 2 and 4)
// and the Revise forms on the campaign page (§2.4), against migration
// 0061's endpoints (web/API-GAPS.md G-17, G-20):
//
//   GET  /v1/targeting/categories      the one category vocabulary (targeting.go:35-66)
//   GET  /v1/targeting/publishers?q=   suitability-exclusion picker (:68-109)
//   GET  /v1/targeting/inventory       which pricing models are reachable (:111-154)
//   GET  /v1/creative-formats          the two formats (creative_validate.go:442-452)
//   POST /v1/creatives/validate        read-only pre-validation (:424-440)

/** The only two formats the creative write accepts (creative_validate.go:80-85); "text" is refused. */
export const FORMAT_KEYS: readonly CreativeFormatKey[] = ["sponsored_listing", "contextual_note"];

export function isFormatKey(v: string): v is CreativeFormatKey {
  return (FORMAT_KEYS as readonly string[]).includes(v);
}

const FALLBACK_FORMATS: Record<CreativeFormatKey, { label: string; description: string }> = {
  sponsored_listing: { label: "Sponsored listing", description: "A title, a short body and a link, labeled sponsored." },
  contextual_note: { label: "Contextual note", description: "A one-line note related to the tool result, labeled sponsored." },
};

/** Settles `value` after `ms` without changes. */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

// ---- targeting ---------------------------------------------------------------

/** Why a category can't be chosen, or what choosing it means. */
function categoryHint(c: TargetingCategory): string | undefined {
  if (!c.available) return "Regulated, and not cleared for targeting on this platform yet, so it can't be chosen.";
  if (c.requires_review) return "Regulated: a reviewer confirms this category explicitly before approval.";
  return undefined;
}

export function CategoryPicker({
  value,
  onChange,
  disabled,
  error,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  error?: string;
}) {
  const q = useTargetingCategories();
  return (
    <fieldset className="radio-group">
      <legend>Categories</legend>
      <p className="small muted">
        The categories the publisher's tool call is about. Choose none to serve in every context; choosing some means the ad serves
        only when the call's category is one of them.
      </p>
      {q.isPending ? (
        <SkeletonBlock label="Loading the targeting categories" lines={3} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className="stack-sm">
          {q.data.categories.map((c) => (
            <Checkbox
              key={c.key}
              label={
                <>
                  {c.label}
                  {c.regulated ? (
                    <>
                      {" "}
                      <StatusPill tone={c.available ? "warning" : "neutral"}>{c.available ? "Regulated" : "Not available"}</StatusPill>
                    </>
                  ) : null}
                </>
              }
              hint={categoryHint(c)}
              checked={value.includes(c.key)}
              disabled={disabled || (!c.available && !value.includes(c.key))}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, c.key] : value.filter((k) => k !== c.key))
              }
            />
          ))}
          <p className="small" role="status">
            {value.length === 0
              ? "No categories chosen: the campaign can serve in any context."
              : `Serves only in: ${value.map((k) => categoryLabel(q.data.categories, k)).join(", ")}.`}
          </p>
        </div>
      )}
      {error ? (
        <div className="field-error" role="alert">
          <Icon name="error" className="field-error-icon" />
          <span>{error}</span>
        </div>
      ) : null}
    </fieldset>
  );
}

/** The chosen categories that the catalog says can't be targeted (retired or not cleared). */
export function unavailableCategories(chosen: string[], categories: TargetingCategory[] | undefined): string[] {
  if (!categories) return [];
  return chosen.filter((k) => !categories.find((c) => c.key === k && c.available));
}

export function InventoryHint({ categories, pricingModel }: { categories: string[]; pricingModel: PricingModel | "" }) {
  const ep = useEndpoints();
  const key = [...categories].sort().join(",");
  const q = useQuery({
    queryKey: ["targeting-inventory", key],
    queryFn: () => ep.advertiser.getTargetingInventory(key ? key.split(",") : []),
    enabled: categories.length <= 64,
    retry: false,
  });
  if (!q.data) return null;
  const yes = (b: boolean) => (b ? "available" : "none right now");
  const missing = pricingModel !== "" && !q.data[pricingModel];
  return (
    <div className="stack-sm">
      <p className="small muted" role="status">
        Inventory: CPM {yes(q.data.cpm)}, CPE {yes(q.data.cpe)}.{" "}
        {q.data.basis === "any_active_slot"
          ? "This only says whether any active slot of each pricing model exists — sites don't declare categories yet, so it can't be narrowed by your categories."
          : null}
      </p>
      {missing ? (
        <Banner tone="warning" title={`No active ${pricingModel.toUpperCase()} slots right now`}>
          A {pricingModel.toUpperCase()} campaign can be submitted, but won't serve until a publisher offers {pricingModel.toUpperCase()} slots.
        </Banner>
      ) : null}
    </div>
  );
}

export function PublisherExclusions({
  value,
  onChange,
  disabled,
  error,
}: {
  value: TargetingPublisher[];
  onChange: (next: TargetingPublisher[]) => void;
  disabled?: boolean;
  error?: string;
}) {
  const ep = useEndpoints();
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), 300);
  const tooShort = q.length > 0 && q.length < 2;
  const tooLong = q.length > 253;
  const search = useQuery({
    queryKey: ["targeting-publishers", q.toLowerCase()],
    queryFn: () => ep.advertiser.searchTargetingPublishers(q),
    enabled: q.length >= 2 && !tooLong,
    retry: false,
  });
  const excluded = new Set(value.map((p) => p.publisher_id));
  return (
    <div className="stack-sm">
      <Input
        label="Never show on these publishers"
        optional
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        hint={
          tooShort
            ? "Type at least 2 characters of a site domain."
            : "Search by site domain. Publishers are shown by their sites, never by name."
        }
        error={tooLong ? "Search with at most 253 characters." : error}
      />
      {search.isError ? <ErrorState error={search.error} onRetry={() => void search.refetch()} /> : null}
      {search.data ? (
        search.data.publishers.length === 0 ? (
          <p className="small muted">No active sites match “{q}”.</p>
        ) : (
          <ul className="stack-sm" aria-label="Matching publishers">
            {search.data.publishers.map((p) => (
              <li key={p.publisher_id} className="row">
                <span className="break-all">
                  <strong>{p.display_name}</strong>
                  {p.sites.length > 1 ? <span className="xs muted"> and {p.sites.slice(1).join(", ")}</span> : null}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={disabled || excluded.has(p.publisher_id)}
                  onClick={() => onChange([...value, p])}
                >
                  {excluded.has(p.publisher_id) ? "Excluded" : "Exclude"} <span className="visually-hidden">{p.display_name}</span>
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}
      {value.length > 0 ? (
        <div className="stack-sm">
          <span className="field-label">Excluded publishers</span>
          <ul className="stack-sm" aria-label="Excluded publishers">
            {value.map((p) => (
              <li key={p.publisher_id} className="row">
                <span className="break-all">{p.display_name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => onChange(value.filter((x) => x.publisher_id !== p.publisher_id))}
                >
                  Remove <span className="visually-hidden">{p.display_name}</span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ---- formats -----------------------------------------------------------------

export function FormatPicker({
  value,
  onChange,
  disabled,
}: {
  value: CreativeFormatKey | "";
  onChange: (v: CreativeFormatKey) => void;
  disabled?: boolean;
}) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["creative-formats"], queryFn: () => ep.advertiser.listCreativeFormats(), staleTime: Infinity });
  // Only the two keys the write accepts are ever offered, whatever else a
  // response might carry; the static copy covers a failed read.
  const options = FORMAT_KEYS.map((key) => {
    const f = q.data?.formats.find((x) => x.key === key);
    return { value: key, label: f?.label ?? FALLBACK_FORMATS[key].label, hint: f?.description ?? FALLBACK_FORMATS[key].description };
  });
  return <RadioGroup<CreativeFormatKey> legend="Format" name="format" value={value} onChange={(v) => (disabled ? undefined : onChange(v))} options={options} />;
}

// ---- creative pre-validation (G-20) -------------------------------------------

export function validationKey(req: CreativeValidationRequest) {
  return ["creative-validation", req.title, req.body, req.format, req.destination_url] as const;
}

/** The server's answer for exactly this input: cached, so Continue after the live check doesn't ask twice. */
export function fetchValidation(qc: QueryClient, ep: Endpoints, req: CreativeValidationRequest): Promise<CreativeValidation> {
  return qc.fetchQuery({ queryKey: validationKey(req), queryFn: () => ep.advertiser.validateCreative(req), staleTime: 30_000 });
}

/** Live validation, 500 ms after typing stops. `settled` is false while the input is newer than the answer. */
export function useLiveValidation(req: CreativeValidationRequest, enabled: boolean) {
  const ep = useEndpoints();
  // Debounce the serialized request: the object is new on every render, so
  // debouncing it directly would re-arm the timer forever.
  const current = JSON.stringify(req);
  const settledJson = useDebounced(current, 500);
  const settledReq = JSON.parse(settledJson) as CreativeValidationRequest;
  const any = !!(settledReq.title || settledReq.body || settledReq.destination_url);
  const q = useQuery({
    queryKey: validationKey(settledReq),
    queryFn: () => ep.advertiser.validateCreative(settledReq),
    enabled: enabled && any,
    staleTime: 30_000,
    retry: false,
  });
  return { query: q, settled: settledJson === current };
}

export function blockingIssues(v: CreativeValidation): ValidationIssue[] {
  return v.issues.filter((i) => i.severity === "error");
}

function num(d: Record<string, unknown> | undefined, k: string): number | undefined {
  const v = d?.[k];
  return typeof v === "number" ? v : undefined;
}

/** Plain-language text for a validation issue code (creative_validate.go). The policy category is named, never the matched text. */
export function issueMessage(i: ValidationIssue, host?: string | null): string {
  switch (i.code) {
    case "title_required":
      return "Enter a title.";
    case "body_required":
      return "Enter the body text.";
    case "unknown_format":
      return "Choose a format: sponsored listing or contextual note.";
    case "envelope_too_large": {
      const bytes = num(i.detail, "envelope_bytes");
      const max = num(i.detail, "max_envelope_bytes") ?? 2048;
      const over = num(i.detail, "over_by") ?? (bytes !== undefined ? bytes - max : undefined);
      return `The ad is ${over ?? "some"} ${over === 1 ? "byte" : "bytes"} over the ${max.toLocaleString("en-US")}-byte limit publishers' SDKs accept${
        bytes !== undefined ? ` (${bytes.toLocaleString("en-US")} bytes)` : ""
      }. An SDK drops an oversized ad rather than shortening it, so shorten the title or body.`;
    }
    case "unsafe_characters":
      return "Contains invisible or control characters. Publishers' SDKs replace them with spaces, so the ad wouldn't read as written.";
    case "policy_flag": {
      const cat = typeof i.detail?.category === "string" ? i.detail.category : "";
      if (cat === "imperative_tool_invocation")
        return "Reads like an instruction to an AI tool or assistant. A reviewer will look at it; ads must describe, not instruct.";
      if (cat === "disclosure_evasion") return "Reads as if it weren't an ad. A reviewer will look at it; ads are always labeled sponsored.";
      return `Flagged for a reviewer (${cat.replace(/_/g, " ") || "content policy"}).`;
    }
    case "destination_url_required":
      return "Enter the destination URL.";
    case "destination_url_invalid":
      return "Enter a full URL, starting with https://";
    case "destination_not_https":
      return "The destination must use https://";
    case "destination_has_userinfo":
      return "The URL can't contain a username or password.";
    case "destination_ip_literal":
      return "Use a domain name, not an IP address: an IP address can't prove domain control.";
    case "destination_ip_class_blocked":
      return "That address points at a private or reserved network, which ads can't link to.";
    case "destination_host_invalid":
      return "Use a public domain name.";
    case "destination_url_shortener_refused":
      return "Shortened or forwarding links aren't allowed. Link directly to the page — copy the URL from your browser's address bar once you're on it, not a share link.";
    case "destination_dns_unresolved":
      return "The domain doesn't resolve right now. It can still be submitted; review checks it again.";
    case "domain_verification_required":
      return `You'll prove you control ${host ?? "this domain"} with a DNS record after submitting. Review can't approve the creative until you do.`;
    default:
      return `${i.code.replace(/_/g, " ")}.`;
  }
}

function tri(v: boolean | null, yes: string, no: string): string {
  return v === null ? "not checked" : v ? yes : no;
}

export function CreativeValidationPanel({ req, enabled = true }: { req: CreativeValidationRequest; enabled?: boolean }) {
  const { query: q, settled } = useLiveValidation(req, enabled);
  const any = !!(req.title || req.body || req.destination_url);
  if (!any) return null;
  const v = q.data;
  const errors = v ? v.issues.filter((i) => i.severity === "error") : [];
  const warnings = v ? v.issues.filter((i) => i.severity === "warning") : [];
  const review = v ? v.issues.filter((i) => i.severity === "review") : [];
  const checking = !settled || q.isFetching;
  return (
    <section className="stack-sm" aria-label="Validation">
      <h3>Validation</h3>
      <p className="small" role="status" aria-live="polite">
        {checking && !v
          ? "Checking…"
          : q.isError
            ? "Couldn't check the creative right now. Continue checks it again."
            : v
              ? v.valid
                ? `Passed initial validation — queued for full review once submitted.${checking ? " Re-checking your changes…" : ""}`
                : `${errors.length} ${errors.length === 1 ? "problem" : "problems"} to fix before continuing.${checking ? " Re-checking your changes…" : ""}`
              : null}
      </p>
      {v ? (
        <>
          <p className="small">
            Envelope size: <strong>{v.envelope_bytes.toLocaleString("en-US")}</strong> of {v.max_envelope_bytes.toLocaleString("en-US")} bytes
            <span className="xs muted"> — measured the way publishers' SDKs measure the served ad, including its link and label.</span>
          </p>
          {errors.length ? (
            <Banner tone="error" title="Must fix">
              <ul>
                {errors.map((i, n) => (
                  <li key={`${i.code}-${n}`}>{issueMessage(i, v.destination.host)}</li>
                ))}
              </ul>
            </Banner>
          ) : null}
          {review.length ? (
            <Banner tone="info" title="A reviewer will look at this">
              <ul>
                {review.map((i, n) => (
                  <li key={`${i.code}-${n}`}>{issueMessage(i, v.destination.host)}</li>
                ))}
              </ul>
            </Banner>
          ) : null}
          {warnings.length ? (
            <Banner tone="warning" title="Worth knowing (doesn't block submitting)">
              <ul>
                {warnings.map((i, n) => (
                  <li key={`${i.code}-${n}`}>{issueMessage(i, v.destination.host)}</li>
                ))}
              </ul>
            </Banner>
          ) : null}
          {v.destination.host ? (
            <p className="xs muted">
              Destination <code>{v.destination.host}</code>: https {v.destination.https ? "yes" : "no"}, DNS{" "}
              {tri(v.destination.dns_ok, "resolves", "doesn't resolve")}, network {tri(v.destination.ip_class_ok, "public", "blocked")}, domain
              control {tri(v.destination.domain_verified, "proven", "not proven yet")}.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
